import React, { useState } from 'react';
import { signIn, resetPassword, confirmResetPassword, confirmSignIn } from 'aws-amplify/auth';
import './AuthForm.css';

const AuthForm = ({ onAuthSuccess }) => {
  const [mode, setMode] = useState('signin'); // signin, forgot, resetConfirm, newPassword
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    code: ''
  });
  const [, setChallengeUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (mode === 'signin' && !formData.password) {
      newErrors.password = 'Password is required';
    }

    if (mode === 'newPassword') {
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters';
      }
      
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (mode === 'resetConfirm') {
      if (!formData.code) {
        newErrors.code = 'Confirmation code is required';
      }
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setMessage('');

    try {
      switch (mode) {
        case 'signin':
          const signInResult = await signIn({
            username: formData.email,
            password: formData.password
          });
          
          if (signInResult.isSignedIn) {
            onAuthSuccess();
          } else if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            setChallengeUser(signInResult);
            setMode('newPassword');
            setMessage('Please set a new password for your account.');
          }
          break;

        case 'newPassword':
          await confirmSignIn({
            challengeResponse: formData.newPassword
          });
          setMessage('Password updated successfully!');
          onAuthSuccess();
          break;

        case 'forgot':
          await resetPassword({
            username: formData.email
          });
          setMessage('Please check your email for a reset code.');
          setMode('resetConfirm');
          break;

        case 'resetConfirm':
          await confirmResetPassword({
            username: formData.email,
            confirmationCode: formData.code,
            newPassword: formData.newPassword
          });
          setMessage('Password reset successful! Please sign in.');
          setMode('signin');
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Auth error:', error);
      setErrors({ general: error.message || 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormTitle = () => {
    switch (mode) {
      case 'signin': return 'Admin Portal';
      case 'newPassword': return 'Set New Password';
      case 'forgot': return 'Reset Password';
      case 'resetConfirm': return 'New Password';
      default: return 'Admin Portal';
    }
  };

  const getFormSubtitle = () => {
    switch (mode) {
      case 'signin': return 'Sign in to access the Aethos admin dashboard';
      case 'newPassword': return 'Please set a new password for your account';
      case 'forgot': return 'Enter your email to receive a reset code';
      case 'resetConfirm': return 'Enter your new password and confirmation code';
      default: return 'Sign in to access the Aethos admin dashboard';
    }
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case 'signin': return 'Sign In';
      case 'newPassword': return 'Set Password';
      case 'forgot': return 'Send Reset Code';
      case 'resetConfirm': return 'Reset Password';
      default: return 'Sign In';
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <div className="form-header">
          <h1 className="form-title">{getFormTitle()}</h1>
          <div className="title-underline"></div>
          <p className="form-subtitle">{getFormSubtitle()}</p>
        </div>

        {message && (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <div>
              <h3>Success</h3>
              <p>{message}</p>
            </div>
          </div>
        )}

        {errors.general && (
          <div className="error-banner">
            <div className="error-icon">!</div>
            <div>
              <h3>Error</h3>
              <p>{errors.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="form">
          {mode !== 'newPassword' && (
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your email address"
                disabled={mode === 'resetConfirm'}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>
          )}

          {mode === 'resetConfirm' && (
            <div className="form-group">
              <label htmlFor="code" className="form-label">Confirmation Code</label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                className={`form-input ${errors.code ? 'error' : ''}`}
                placeholder="Enter the 6-digit code"
              />
              {errors.code && <div className="error-message">{errors.code}</div>}
            </div>
          )}

          {mode === 'signin' && (
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter your password"
              />
              {errors.password && <div className="error-message">{errors.password}</div>}
            </div>
          )}

          {(mode === 'newPassword' || mode === 'resetConfirm') && (
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                className={`form-input ${errors.newPassword ? 'error' : ''}`}
                placeholder="Enter your new password"
              />
              {errors.newPassword && <div className="error-message">{errors.newPassword}</div>}
            </div>
          )}

          {(mode === 'newPassword' || mode === 'resetConfirm') && (
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                placeholder="Confirm your new password"
              />
              {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`submit-button ${isLoading ? 'submitting' : ''}`}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Processing...
              </>
            ) : (
              <>
                {getSubmitButtonText()}
                <svg className="submit-arrow" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </>
            )}
          </button>

          <div className="auth-links">
            {mode === 'signin' && (
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="auth-link"
              >
                Forgot your password?
              </button>
            )}

            {(mode === 'forgot' || mode === 'resetConfirm') && (
              <button
                type="button"
                onClick={() => setMode('signin')}
                className="auth-link"
              >
                Back to sign in
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthForm;

